import { useEffect, useState } from "react";
import {
  listStudioJobs,
  getStudioStats,
  getStudioProviders,
  type StudioJobSummary,
  type StudioStats,
} from "../../client";

type JobBrowserProps = {
  onSelectJob: (job: StudioJobSummary) => void;
  selectedDirectory?: string | null;
};

export function JobBrowser({ onSelectJob, selectedDirectory }: JobBrowserProps) {
  const [jobs, setJobs] = useState<StudioJobSummary[]>([]);
  const [stats, setStats] = useState<StudioStats | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [hasVideoFilter, setHasVideoFilter] = useState<"all" | "yes" | "no">("all");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadJobs();
  }, [search, selectedProvider, hasVideoFilter]);

  const loadData = async () => {
    try {
      const [statsData, providersData] = await Promise.all([
        getStudioStats(),
        getStudioProviders(),
      ]);
      setStats(statsData);
      setProviders(providersData);
    } catch (err) {
      console.error("Failed to load studio data:", err);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listStudioJobs({
        search: search || undefined,
        provider: selectedProvider || undefined,
        has_video: hasVideoFilter === "all" ? undefined : hasVideoFilter === "yes",
        limit: 100,
      });
      setJobs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div style={{ color: "#e0e0e0" }}>
      {/* Header */}
      <div style={{ marginBottom: "1rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>Job Browser</h2>
        {stats && (
          <div style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.25rem" }}>
            {stats.total_jobs} jobs | {stats.jobs_with_video} with video | {stats.total_sections} sections
          </div>
        )}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by topic..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          padding: "0.75rem",
          marginBottom: "0.5rem",
          backgroundColor: "#1a1a1a",
          border: "1px solid #333",
          borderRadius: "6px",
          color: "#e0e0e0",
          fontSize: "1rem",
        }}
      />

      {/* Filters */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <select
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value)}
          style={{
            flex: "1 1 45%",
            minWidth: "120px",
            padding: "0.625rem 0.5rem",
            backgroundColor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "6px",
            color: "#e0e0e0",
            fontSize: "0.875rem",
          }}
        >
          <option value="">All Providers</option>
          {providers.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <select
          value={hasVideoFilter}
          onChange={(e) => setHasVideoFilter(e.target.value as "all" | "yes" | "no")}
          style={{
            flex: "1 1 45%",
            minWidth: "120px",
            padding: "0.625rem 0.5rem",
            backgroundColor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "6px",
            color: "#e0e0e0",
            fontSize: "0.875rem",
          }}
        >
          <option value="all">All Jobs</option>
          <option value="yes">With Video</option>
          <option value="no">No Video</option>
        </select>
      </div>

      {/* Job List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>Loading...</div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#f44336" }}>{error}</div>
      ) : jobs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>No jobs found</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {jobs.map((job) => (
            <div
              key={job.directory}
              onClick={() => onSelectJob(job)}
              style={{
                padding: "0.75rem",
                backgroundColor: selectedDirectory === job.directory ? "#2a2a2a" : "#151515",
                border: selectedDirectory === job.directory ? "1px solid #4a90d9" : "1px solid #333",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (selectedDirectory !== job.directory) {
                  e.currentTarget.style.backgroundColor = "#1e1e1e";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedDirectory !== job.directory) {
                  e.currentTarget.style.backgroundColor = "#151515";
                }
              }}
            >
              {/* Title */}
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: "0.25rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={job.topic}
              >
                {job.topic}
              </div>

              {/* Meta */}
              <div style={{ display: "flex", gap: "0.5rem", fontSize: "0.7rem", color: "#888" }}>
                <span>{job.section_count} sections</span>
                <span>•</span>
                <span>{formatDate(job.modified_at)}</span>
              </div>

              {/* Status badges */}
              <div style={{ display: "flex", gap: "0.25rem", marginTop: "0.375rem" }}>
                {job.final_video && (
                  <span
                    style={{
                      padding: "0.125rem 0.375rem",
                      backgroundColor: "#1b5e20",
                      borderRadius: "3px",
                      fontSize: "0.65rem",
                      color: "#a5d6a7",
                    }}
                  >
                    Video
                  </span>
                )}
                {job.has_outline && (
                  <span
                    style={{
                      padding: "0.125rem 0.375rem",
                      backgroundColor: "#1a237e",
                      borderRadius: "3px",
                      fontSize: "0.65rem",
                      color: "#9fa8da",
                    }}
                  >
                    Outline
                  </span>
                )}
                {job.has_storyboard && (
                  <span
                    style={{
                      padding: "0.125rem 0.375rem",
                      backgroundColor: "#4a148c",
                      borderRadius: "3px",
                      fontSize: "0.65rem",
                      color: "#ce93d8",
                    }}
                  >
                    Storyboard
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
